import { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Cake } from "lucide-react";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const next = location.state?.next || "/order";
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setErr("");
    try {
      await register(form);
      toast.success("Account created! 🎉");
      navigate(next);
    } catch (e2) {
      setErr(apiError(e2.response?.data?.detail) || e2.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="App">
      <Navbar />
      <div className="mx-auto flex max-w-md flex-col px-6 py-16">
        <div className="mb-8 text-center">
          <span className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-sugar-pink text-white"><Cake size={26} /></span>
          <h1 className="mt-4 font-display text-3xl font-extrabold text-sugar-ink">Create your account</h1>
          <p className="mt-1 text-sm text-sugar-ink2">So you can order & track every cake</p>
        </div>
        <form onSubmit={submit} className="space-y-4 rounded-2xl border border-sugar-border bg-white p-7 sugar-card-shadow">
          {err && <p data-testid="register-error" className="rounded-lg bg-red-50 px-3 py-2 text-sm font-medium text-red-600">{err}</p>}
          <div>
            <label className="mb-1.5 block text-sm font-bold text-sugar-ink">Full name</label>
            <Input data-testid="register-name" required value={form.name} onChange={set("name")} className="rounded-xl border-sugar-border" />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-bold text-sugar-ink">Email</label>
            <Input data-testid="register-email" type="email" required value={form.email} onChange={set("email")} className="rounded-xl border-sugar-border" />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-bold text-sugar-ink">Phone</label>
            <Input data-testid="register-phone" value={form.phone} onChange={set("phone")} className="rounded-xl border-sugar-border" />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-bold text-sugar-ink">Password</label>
            <Input data-testid="register-password" type="password" required minLength={6} value={form.password} onChange={set("password")} className="rounded-xl border-sugar-border" />
          </div>
          <Button data-testid="register-submit" disabled={loading} type="submit" className="w-full rounded-full bg-sugar-pink py-6 font-bold text-white hover:bg-sugar-pinkhover">
            {loading ? "Creating..." : "Create account"}
          </Button>
          <p className="text-center text-sm text-sugar-ink2">
            Already have an account? <Link data-testid="register-to-login" to="/login" state={{ next }} className="font-bold text-sugar-pink">Log in</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
